'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

const FEATURED_JOURNEYS = [
  { name: 'Italian Lakes & Amalfi Honeymoon', location: 'Italy', nights: '12 nights, private', price: 'From $22,800 per guest', tags: ['Honeymoon', 'Italy', 'Private'] },
  { name: 'Royal Rajasthan Private Tour', location: 'India', nights: '13 nights, private', price: 'From $24,800 per guest', tags: ['India', 'Palaces', 'Private'] },
  { name: 'Japan Family Cherry Blossom', location: 'Japan', nights: '14 nights, private', price: 'From $38,400 per guest', tags: ['Japan', 'Family', 'Cherry Blossom'] },
  { name: 'Southern Africa Private Fly-In Safari', location: 'Botswana, Zambia, South Africa', nights: '14 nights, private', price: 'From $46,500 per guest', tags: ['Safari', 'Fly-In', 'Multi-Country'] },
];

const FEATURES = [
  { icon: '👤', title: 'A dedicated specialist', desc: 'One senior planner from concept to farewell.' },
  { icon: '🗓', title: 'Total flexibility', desc: 'Choose your date, your pace, your properties, your guides.' },
  { icon: '📞', title: 'Concierge on the ground', desc: '24/7 in-country support anywhere in the world.' },
];

const DESTINATIONS = [
  { name: 'Japan', icon: '🇯🇵', desc: 'Cherry blossoms, ryokans, Michelin dining' },
  { name: 'India', icon: '🇮🇳', desc: 'Maharaja palaces, tiger reserves, spice routes' },
  { name: 'Italy', icon: '🇮🇹', desc: 'Amalfi, Tuscany, Venetian canals' },
  { name: 'Patagonia', icon: '🏔', desc: 'Torres del Paine, glaciers, gaucho culture' },
  { name: 'Morocco', icon: '🇲🇦', desc: 'Medinas, Sahara camps, Atlas mountains' },
  { name: 'Bhutan', icon: '🇧🇹', desc: 'Tiger\'s Nest, dzongs, high-altitude trekking' },
  { name: 'Peru', icon: '🇵🇪', desc: 'Machu Picchu, Amazon, Sacred Valley' },
  { name: 'New Zealand', icon: '🇳🇿', desc: 'Fiordland, Queenstown, Māori culture' },
];

export default function TailorMadePage() {
  const router = useRouter();

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--surface-2)]/95 backdrop-blur-xl border-b border-[var(--border)] h-16 flex items-center px-6">
        <Link href="/" className="text-[var(--primary)] font-bold tracking-widest uppercase mr-8" style={{ fontFamily: 'var(--font-serif)' }}>WorldwayLuxe</Link>
        <div className="flex items-center gap-6 text-sm text-[var(--muted-foreground)]">
          <Link href="/safari" className="hover:text-[var(--primary)] transition-colors">Safari</Link>
          <Link href="/villas" className="hover:text-[var(--primary)] transition-colors">Villas</Link>
          <Link href="/cruises" className="hover:text-[var(--primary)] transition-colors">Cruises</Link>
          <Link href="/rail" className="hover:text-[var(--primary)] transition-colors">Luxury Rail</Link>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Link href="/auth/login" className="text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] px-4 py-2">Sign In</Link>
          <Link href="/auth/register" className="btn-primary text-sm px-5 py-2 rounded-lg">Join Now</Link>
        </div>
      </nav>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[var(--background)] to-[#0D1525]" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(201,168,76,0.15) 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <p className="label-caps mb-3 text-[var(--primary)]">Written for you</p>
          <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
            Tailor-Made Journeys<br />
            <span className="gold-text-gradient">Every Element Crafted for You.</span>
          </h1>
          <p className="text-lg text-[var(--muted-foreground)] mb-6 max-w-xl">
            Every element crafted around you — your interests, your travel companions, your pace. Speak with a specialist and receive a bespoke itinerary within 72 hours.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {FEATURES?.map((f) => (
              <div key={f?.title} className="glass-card rounded-xl p-4 border border-[rgba(201,168,76,0.1)]">
                <div className="text-2xl mb-2">{f?.icon}</div>
                <div className="text-sm font-bold mb-1">{f?.title}</div>
                <div className="text-xs text-[var(--muted-foreground)]">{f?.desc}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-4">
            <button onClick={() => router?.push('/booking?vertical=custom_itinerary')} className="btn-primary px-8 py-3 rounded-xl text-sm font-semibold">Design Your Journey</button>
            <Link href="/ai-concierge" className="btn-ghost px-8 py-3 rounded-xl text-sm font-semibold border border-[var(--border)]">Ask AI Concierge</Link>
          </div>
        </div>
      </section>
      {/* Featured Journeys */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Featured Journeys</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>Bespoke <span className="gold-text-gradient">Tailor-Made Experiences</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FEATURED_JOURNEYS?.map((j) => (
              <div key={j?.name} className="glass-card rounded-xl p-5 hover:border-[rgba(201,168,76,0.3)] transition-all group cursor-pointer" onClick={() => router?.push('/booking?vertical=custom_itinerary')}>
                <div className="text-2xl mb-3">✨</div>
                <h3 className="text-base font-bold mb-1 group-hover:text-[var(--primary)] transition-colors" style={{ fontFamily: 'var(--font-serif)' }}>{j?.name}</h3>
                <p className="text-xs text-[var(--muted-foreground)] mb-1">{j?.location}</p>
                <p className="text-xs text-[var(--muted-foreground)] mb-3">{j?.nights}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {j?.tags?.map((tag) => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(201,168,76,0.1)] text-[var(--primary)]">{tag}</span>
                  ))}
                </div>
                <p className="text-xs font-semibold text-[var(--primary)]">{j?.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Destinations */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Popular Destinations</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>Where Would You <span className="gold-text-gradient">Like to Go?</span></h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {DESTINATIONS?.map((d) => (
              <button key={d?.name} onClick={() => router?.push(`/booking?vertical=custom_itinerary&destination=${d?.name}`)} className="glass-card rounded-xl p-4 text-center hover:border-[rgba(201,168,76,0.3)] transition-all group">
                <div className="text-2xl mb-2">{d?.icon}</div>
                <div className="text-sm font-bold mb-1 group-hover:text-[var(--primary)] transition-colors">{d?.name}</div>
                <div className="text-[10px] text-[var(--muted-foreground)]">{d?.desc}</div>
              </button>
            ))}
          </div>
        </div>
      </section>
      {/* FAQ */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-6">FAQ</p>
          <div className="space-y-4">
            {[
              { q: 'How does the tailor-made process work?', a: 'A first proposal within 72 hours; most journeys refined over 2–3 iterations. You\'ll work with one dedicated senior specialist throughout.' },
              { q: 'Can I change my itinerary after booking?', a: 'Total flexibility is our promise. We accommodate changes wherever possible and will always advise on the best options.' },
              { q: 'What is the minimum budget?', a: 'Our tailor-made journeys typically start from $5,000 per person. Contact us and we\'ll craft the best possible experience within your budget.' },
            ]?.map((faq) => (
              <div key={faq?.q} className="glass-card rounded-xl p-5">
                <div className="text-sm font-bold mb-2 text-[var(--primary)]">{faq?.q}</div>
                <div className="text-xs text-[var(--muted-foreground)] leading-relaxed">{faq?.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <footer className="border-t border-[var(--border)] py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[var(--muted-foreground)]">© 2026 Worldway Travels Group · All rights reserved.</div>
          <div className="flex items-center gap-6 text-xs text-[var(--muted-foreground)]">
            <Link href="/" className="hover:text-[var(--primary)] transition-colors">Home</Link>
            <Link href="/safari" className="hover:text-[var(--primary)] transition-colors">Safari</Link>
            <Link href="/villas" className="hover:text-[var(--primary)] transition-colors">Villas</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
